import React, { useState } from 'react';
import {
  CompanyUpdateSubscriptionDto,
  OrganisationDto,
  OrganisationDetailDto,
  UserDto,
  UpdateOrganisationRequestDto,
} from 'models/dtoTypes';
import { CallbackAttributes, CompanyListAttributes, UserAttributes } from 'kosmos/models';
import axios from 'axios';
import { get } from 'lodash';
import { date2str } from 'utils/date';
import {
  GetPaginatedAuditLogs,
  DisableOrganisation,
  EnableOrganisation,
  GetListScoredCompanies,
  GetAllAuditLogs,
} from 'services/openapiClient';
import { GetUsers, CreateUser, DisableUser, EnableUser } from 'services/user';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import ResourceTable from 'kosmos/components/ResourceTable';
import ResourceFullListTable from 'kosmos/components/ResourceFullListTable';
import Notice from 'kosmos/components/Notice';
import { GetOrganisation, UpdateOrganisation, GetAuditLogSummary } from 'services/openapiClient';
import generateNonce from 'utils/generateNonce';
import CreateResourceModal from 'kosmos/dialogs/CreateResourceModal';
import CreditUpdateWidget from './_creditUpdateWidget';
import CurrentUser from 'auth/currentUser';
import {
  auditLogFilterSchema,
  callbackCreateSchema,
  companyListCreateSchema,
  generateActivityColumns,
  generateAuditLogSummaryColumns,
  generateCallbackColumns,
  generateCompanyListColumns,
  generateScoredCompaniesColumns,
  generateUserColumns,
  scoredCompaniesFilterSchema,
  userCreateSchema,
} from './_organisationSchema';
import { CreateCompanyList, GetCompanyLists } from 'services/companyList';
import { EFeature } from 'auth/Authentication';
import ScoredCompaniesResourceTable from './_scoredCompaniesTable';
import { getZoneFromCountryCode } from 'utils/countries';
import { useTranslation } from 'react-i18next';
import {
  CreateOrganisationCallback,
  DeleteOrganisationCallback,
  GetOrganisationCallback,
} from 'services/organisation';

const PREFIX = 'OrganisationDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

interface IFilters {
  [key: string]: string;
}

function OrganisationDetailPage(props: { id: string }): JSX.Element {
  const id = props.id;
  const [openCreateUser, setOpenCreateUser] = React.useState(false);
  const [openCreateCompanyList, setOpenCreateCompanyList] = React.useState(false);
  const [openCreateCallback, setOpenCreateCallback] = React.useState(false);

  const [openNotice, setOpenNotice] = React.useState(false);
  const [tabId, setTabId] = React.useState('details');
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [auditFilters, setAuditFilters] = useState<IFilters>({});
  const { t } = useTranslation('default');

  const handleDownloadBillingReport = async () => {
    const filters = [
      { column: 'organisationId', value: id },
      { column: 'logType', value: 'billing' },
    ];
    if (auditFilters.createdMonth) {
      filters.push({
        column: 'createdMonth',
        value: auditFilters.createdMonth,
      });
    }

    const auditLogs = await GetAllAuditLogs({
      filters,
      sortColumn: 'created',
      isDesc: false,
    });

    const csvHeaders = 'Action name, Timestamp, Company Name, Country, Zone\n';
    const csvRows: string[] = auditLogs.map((item) => {
      let meta = {};
      try {
        meta = JSON.parse(item.metadata || '');
      } catch (e) {
        // ignore if meta has no data
      }
      let companyName: string = get(meta, ['CompanyName']) || '';
      if (companyName.includes('"')) {
        // eslint-disable-next-line
        companyName = companyName.replace(/"/g, "'");
      }
      companyName = `"${companyName}"`; // wrap name with "
      const companyCountry = get(meta, ['Country']) || '';
      const zone = getZoneFromCountryCode(companyCountry);
      const timestamp = item.created ? new Date(item.created).toISOString() : '';
      return [item.actionName, timestamp, companyName, companyCountry, zone].join(',');
    });

    const csvContent = `data:text/csv;charset=utf-8,${csvHeaders}${csvRows.join('\n')}`;

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute(
      'download',
      `org_${id}${auditFilters.createdMonth ? `_${auditFilters.createdMonth}` : ''}.csv`,
    );
    document.body.appendChild(link); // Required for FF

    link.click();
    link.remove();
  };

  const handleCloseCreatedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenNotice(false);
  };

  const handleDisableOrganisation = async () => {
    if (!id) return;
    try {
      await DisableOrganisation(id);
      setNonce(generateNonce());
    } catch (e) {
      handleError(e);
    }
  };

  const handleEnableOrganisation = async () => {
    if (!id) return;
    try {
      await EnableOrganisation(id);
      setNonce(generateNonce());
    } catch (e) {
      handleError(e);
    }
  };

  const handleDisableUser = async (user: UserDto) => {
    try {
      return await DisableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot disable this user',
        severity: 'error',
      });
    }
  };

  const handleDeleteCallback = async (callback: CompanyUpdateSubscriptionDto) => {
    try {
      const deleted = await DeleteOrganisationCallback(id, callback.id || '');
      setNonce(generateNonce());
      return deleted;
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot delete this callback',
        severity: 'error',
      });
    }
  };

  const handleEnableUser = async (user: UserDto) => {
    try {
      return await EnableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot enable this user',
        severity: 'error',
      });
    }
  };

  const isAdmin = CurrentUser.getInstance().isAdmin();

  const generateTabs = (): ITab<OrganisationDetailDto>[] => [
    {
      id: 'details',
      title: t('details'),
      getUiSchema: () => ({
        fields: [
          {
            label: t('id'),
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('status'),
            name: 'status',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('name'),
            name: 'name',
            widget: 'text',
          },
          {
            hint: 'Organisation name alias',
            label: t('display-name'),
            name: 'displayName',
            widget: 'text',
          },
          {
            hint: 'Organisation logo',
            label: t('Organisation Logo'),
            name: 'logoUrl',
            formatter: (url: string) => {
              return <img src={url} width={250} height="auto" />;
            },
            widget: 'imageUpload',
            readonly: !isAdmin,
          },
          {
            label: 'Has Unlimited Searches?',
            name: 'hasUnlimitedSearches',
            formatter: (checked: boolean) => (checked ? 'Yes' : 'No'),
            readonly: !isAdmin,
            widget: 'checkbox',
          },
          {
            label: 'Daily Searches Consumed',
            name: 'dailyLimitConsumed',
            readonly: true,
            widget: 'text',
          },
          {
            label: 'Daily Search Consumption Limit',
            name: 'dailyConsumptionLimit',
            readonly: !isAdmin,
            widget: 'text',
          },
          {
            label: t('has-unlimited-credits') + '?',
            name: 'hasUnlimitedCredits',
            formatter: (checked: boolean) => (checked ? 'Yes' : 'No'),
            readonly: !isAdmin,
            widget: 'checkbox',
          },
          {
            label: t('is-under-evaluation') + '?',
            name: 'isEvaluating',
            formatter: (checked: boolean) => (checked ? 'Yes' : 'No'),
            readonly: !isAdmin,
            widget: 'checkbox',
          },
          {
            label: t('SmartGovernance Enabled') + '?',
            name: 'smartGovernanceEnabled',
            formatter: (checked: boolean) => (checked ? 'Yes' : 'No'),
            readonly: !isAdmin,
            widget: 'checkbox',
          },
          {
            label: 'Scoring Report CSV Format',
            name: 'companyReportCsvFormat',
            widget: 'select',
            required: true,
            readonly: !isAdmin,
            formatter: (a: string) => (a === 'Concise' ? 'Concise' : 'Detailed'),
            options: [
              { alias: 'Concise', label: 'Concise' },
              { alias: 'Detailed', label: 'Detailed' },
            ],
          },
          {
            label: 'Billing Type',
            name: 'billingType',
            widget: 'select',
            required: true,
            readonly: !isAdmin,
            formatter: (a: string) =>
              a === 'BillOnScoring' ? 'Bill on Scoring' : 'Bill on Viewing Report',
            options: [
              { alias: 'BillOnScoring', label: 'Bill on Scoring' },
              { alias: 'BillOnViewingReport', label: 'Bill on Viewing Report' },
            ],
          },
          {
            label: t('Auto Notify Adverse News') + '?',
            name: 'autoNotifyAdverseNews',
            formatter: (checked: boolean) => (checked ? 'Yes' : 'No'),
            readonly: !isAdmin,
            widget: 'checkbox',
          },
          {
            label: t('created-at'),
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('last-updated-at'),
            name: 'updated',
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text',
          },
        ],
      }),
    },
    {
      id: 'users',
      title: t('users'),
      hide: () => !CurrentUser.getInstance().canManageResource(EFeature.user),
      render: (organisation: OrganisationDto) => {
        const userColumns = generateUserColumns(
          t,
          organisation,
          {
            isAdmin: isAdmin,
          },
          {
            handleDisableUser,
            handleEnableUser,
          },
        );
        return (
          <div>
            <ResourceTable
              columns={userColumns}
              onFetch={GetUsers}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
            />
          </div>
        );
      },
      actions: (organisation) => {
        const isActive = organisation.status === 'Active';
        const msg: string = isActive
          ? t('disabling-this-organisation-will-block-all-its-users-from-signing.')
          : t('enabling-this-organisation-will-unblock-all-its-users-to-sign');
        return [
          {
            label: t('create-user'),
            onClick: () => setOpenCreateUser(true),
            disabled: !isActive,
          },
          {
            label: isActive ? t('disable-organisation') : t('enable-organisation'),
            color: isActive ? 'error' : 'primary',
            onClick: isActive ? handleDisableOrganisation : handleEnableOrganisation,
            hide: () => !isAdmin,
            confirmMessage: msg,
          },
        ];
      },
    },
    {
      id: 'companyLists',
      title: t('company-list'),
      hide: () => !CurrentUser.getInstance().canManageResource(EFeature.companyList),
      render: () => {
        const companyListColumns = generateCompanyListColumns(t);
        return (
          <div>
            <ResourceTable
              columns={companyListColumns}
              onFetch={GetCompanyLists}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
            />
          </div>
        );
      },
      actions: (organisation) => {
        const isActive = organisation.status === 'Active';
        return [
          {
            label: t('create-new-company-list'),
            onClick: () => setOpenCreateCompanyList(true),
            disabled: !isActive,
          },
        ];
      },
    },
    {
      id: 'callbackUrls',
      title: t('Callback Urls'),
      hide: () => !isAdmin,
      render: () => {
        const columns = generateCallbackColumns(t, handleDeleteCallback);
        return (
          <div>
            <ResourceTable
              columns={columns}
              onFetch={(params: any) => GetOrganisationCallback(id, params)}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
            />
          </div>
        );
      },
      actions: (organisation) => {
        const isActive = organisation.status === 'Active';
        return [
          {
            label: t('Add New Callback'),
            onClick: () => setOpenCreateCallback(true),
            disabled: !isActive,
          },
        ];
      },
    },
    {
      id: 'activities',
      title: t('activities'),
      render: () => {
        return (
          <div>
            <ResourceTable
              filterSchema={auditLogFilterSchema(t)}
              columns={generateActivityColumns(t)}
              onFetch={GetPaginatedAuditLogs}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
              defaultSort={{
                sortColumn: 'created',
                isDesc: true,
              }}
            />
          </div>
        );
      },
    },
    {
      id: 'auditLogSummary',
      title: t('audit-dashboard'),
      render: () => {
        return (
          <div>
            <ResourceFullListTable
              onFiltersChange={(filters) => setAuditFilters(filters)}
              filterSchema={auditLogFilterSchema(t)}
              columns={generateAuditLogSummaryColumns(t)}
              onFetch={GetAuditLogSummary}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
            />
          </div>
        );
      },
      actions: () => {
        return [
          {
            label: t('download-billing-report'),
            onClick: handleDownloadBillingReport,
          },
        ];
      },
    },
    {
      id: 'scoredCompanies',
      title: t('scored-companies'),
      render: () => {
        return (
          <div>
            <ScoredCompaniesResourceTable
              filterSchema={scoredCompaniesFilterSchema(t)}
              columns={generateScoredCompaniesColumns(t, id)}
              onFetch={GetListScoredCompanies}
              defaultQuery={[
                {
                  column: 'organisationId',
                  value: id,
                },
              ]}
            />
          </div>
        );
      },
    },
    {
      id: 'credits',
      title: t('creditssubscriptions'),
      hide: () => {
        return !isAdmin;
      },
      render: (organisation: OrganisationDto) => {
        return (
          <div>
            <CreditUpdateWidget
              organisationId={id}
              remainingCredits={organisation.remainingCredits || 0}
              thresholdCredits={organisation.thresholdCredits || 0}
              onUpdateOrganisation={() => setNonce(generateNonce())}
            />
          </div>
        );
      },
    },
  ];

  const handleFetch = async (): Promise<OrganisationDto> => {
    return await GetOrganisation(id);
  };

  const handleUpdate = async (attrs: UpdateOrganisationRequestDto): Promise<OrganisationDto> => {
    return await UpdateOrganisation(id, attrs);
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      // console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setOpenNotice(true);
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setOpenNotice(true);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCreateUser = async (attrs: UserAttributes) => {
    const injectOrg = {
      ...attrs,
      organisationId: id,
    };
    try {
      const createdMessage = await CreateUser(injectOrg);
      setNonce(generateNonce);
      setOpenCreateUser(false);
      setOpenNotice(true);
      setNoticeData({
        message: createdMessage || 'Success',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleCreateCompanyList = async (attrs: CompanyListAttributes) => {
    const injectOrg = {
      ...attrs,
      organisationId: id,
    };
    try {
      await CreateCompanyList(injectOrg);
      setNonce(generateNonce());
      setOpenCreateCompanyList(false);
      setOpenNotice(true);
      setNoticeData({
        message: 'Success!',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleCreateCallback = async (attrs: CallbackAttributes) => {
    const injectOrg = {
      ...attrs,
      organisationId: id,
    };
    try {
      await CreateOrganisationCallback(injectOrg);
      setNonce(generateNonce());
      setOpenCreateCallback(false);
      setOpenNotice(true);
      setNoticeData({
        message: 'Success!',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <Root>
      <ResourceDetailContainer
        key={nonce}
        tabs={generateTabs()}
        breadcrumbText="Organisations"
        breadcrumbUrl="/organisations"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
      />
      <CreateResourceModal<UserAttributes>
        open={openCreateUser}
        label={t('create-a-new-user')}
        schema={userCreateSchema(t)}
        onClose={() => setOpenCreateUser(false)}
        onSave={handleCreateUser}
      />
      <CreateResourceModal<CompanyListAttributes>
        open={openCreateCompanyList}
        label={t('create-a-new-company-list')}
        schema={companyListCreateSchema(t)}
        onClose={() => setOpenCreateCompanyList(false)}
        onSave={handleCreateCompanyList}
      />
      <CreateResourceModal<CallbackAttributes>
        open={openCreateCallback}
        label={t('Add a new callback')}
        schema={callbackCreateSchema(t)}
        onClose={() => setOpenCreateCallback(false)}
        onSave={handleCreateCallback}
      />
      {noticeData && (
        <Notice
          open={openNotice}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseCreatedNotice}
        />
      )}
    </Root>
  );
}

export default OrganisationDetailPage;
